import React from 'react';
import { COPY } from '../../contracts/copyMap';
import { CONVERSION_EVENTS, useTrackViewOnce } from '../../analytics/conversion';
import useStartTrial from '../../hooks/useStartTrial';
import AurumButton from '../oracle/AurumButton';
import ProOfferPanel from './ProOfferPanel';

// The paywall. One statement, one price, one action — deliberately NOT a
// pricing page: no feature grid, no urgency, no countdown.
//
// `reason` selects the line. Every gated surface passes its own feature key so
// the copy names the thing the user actually reached for, rather than a
// generic "upgrade to continue".
//
// Two sizes, and they are different surfaces rather than one scaled:
//
//   full    the paywall IS the page (Membership, the Glowup feedback gate).
//           Renders ProOfferPanel — the same card as the routine sneak peek,
//           so every full-size paywall in the product looks like one thing.
//   compact an inline gate standing in for a single control (EntitlementGate
//           wrapping the Glowup render button). Must stay small enough to sit
//           inside a page that is otherwise unlocked, so it is NOT the card.
//
// ⚠ Neither size opens with a Censor Bar any more. Both used to lead with a
// black LABEL bar reading ACCESS RESTRICTED, which framed a finished product
// as a locked door and put the denial before the offer. Do not put it back.
export default function MembershipCTA({ reason, compact = false }) {
  const body = COPY.MEMBERSHIP_PAYWALL_REASONS[reason] || COPY.MEMBERSHIP_PAYWALL;
  // The reason rides in the surface label so the conversion events (and the
  // console.error on a failed checkout) say WHICH gate this paywall fronted —
  // MembershipCTA renders for scan_unlock, glowup and glowup_render alike.
  const surface = `MembershipCTA:${reason || 'default'}`;

  if (!compact) {
    return (
      <div className="flex w-full min-w-0 justify-center px-5 py-10" data-testid="membership-cta">
        <ProOfferPanel
          headline={body}
          price={COPY.MEMBERSHIP_PRICE}
          label={surface}
          ctaTestId="membership-cta-button"
        />
      </div>
    );
  }

  return <CompactCTA body={body} surface={surface} />;
}

// Inline variant. No medallion, no card, no price — it stands in for one
// control, so it must not out-weigh the page around it. The merchant-of-record
// disclosure is deliberately absent here and ONLY here: this button reaches
// checkout, so the disclosure has to appear before the charge, and it does —
// on the Stripe Checkout page itself, which is where a compact gate hands off.
// The full-size panel shows it inline because it is the whole surface.
function CompactCTA({ body, surface }) {
  const { startTrial, submitting, error } = useStartTrial(surface);

  // ProOfferPanel tracks the full-size view; the compact gate is its own
  // surface (it stands in for a single control on an otherwise-open page), so
  // it stamps the view itself. `compact: true` keeps the two shapes separable.
  useTrackViewOnce(CONVERSION_EVENTS.PAYWALL_VIEWED, {
    surface,
    gate: 'pro_offer',
    compact: true,
  });

  return (
    <div
      className="flex flex-col items-center gap-4 py-8 text-center"
      data-testid="membership-cta"
    >
      <div className="font-censor text-[11px] font-semibold uppercase tracking-[0.28em] text-aurum">
        {COPY.MEMBERSHIP_EYEBROW_PRO}
      </div>

      <p className="m-0 max-w-sm font-display text-lg text-bone">{body}</p>

      <AurumButton
        onClick={startTrial}
        loading={submitting}
        data-testid="membership-cta-button"
      >
        {COPY.MEMBERSHIP_CTA}
      </AurumButton>

      {/* Bone, never red — DESIGN.md forbids red even for errors. */}
      {error && (
        <p className="m-0 font-display text-sm text-bone" role="alert">{error}</p>
      )}
    </div>
  );
}
